import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";

// #3293 - "Add riders": draws riders from the Not drawn pool into the current
// section (or a new one). Searchable by rider number or name, multi-select,
// with kiosk-size tap targets (the whole row is the target, 56px tall).
// Lists ONLY undrawn riders: ended ones (No show / Scratched) are not in the
// pool the caller passes.
export default function AddRidersDialog({ open, pool, sectionName, onClose, onAdd }) {
  const { t } = useTranslation("score");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState([]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(
      (r) =>
        String(r.competitorNumber ?? "").toLowerCase().includes(q) ||
        String(r.riderName ?? "").toLowerCase().includes(q),
    );
  }, [pool, query]);

  const toggle = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const close = () => {
    setQuery("");
    setPicked([]);
    onClose();
  };
  const add = async () => {
    // Keep the order the scribe tapped them in.
    const ok = await onAdd(picked);
    if (ok !== false) close();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" data-testid="add-riders-dialog">
      <DialogTitle>{t("classGrid.sections.addRidersTitle", { section: sectionName })}</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoComplete="off"
          autoFocus
          fullWidth
          size="medium"
          label={t("classGrid.sections.searchLabel")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ "data-testid": "add-riders-search" }}
          sx={{ mb: 1 }}
        />
        {shown.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {pool.length === 0 ? t("classGrid.sections.noneToAdd") : t("classGrid.sections.noMatch")}
          </Typography>
        )}
        <List disablePadding>
          {shown.map((r) => {
            const checked = picked.includes(r.id);
            return (
              <ListItemButton
                key={r.id}
                onClick={() => toggle(r.id)}
                selected={checked}
                sx={{ minHeight: 56 }}
                data-testid={`add-riders-item-${r.competitorNumber ?? r.id}`}
              >
                <ListItemIcon>
                  <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple inputProps={{ "aria-label": `${r.competitorNumber ?? ""} ${r.riderName ?? ""}` }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {r.competitorNumber ?? "—"} {r.riderName ?? ""}
                    </Box>
                  }
                  secondary={r.horseName ?? ""}
                />
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={close} sx={{ minHeight: 48 }}>
          {t("classGrid.sections.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={picked.length === 0}
          onClick={add}
          sx={{ minHeight: 48 }}
          data-testid="add-riders-confirm"
        >
          {t("classGrid.sections.addSelected", { count: picked.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
